import React from 'react';
import WizardFormFirstPage from './RegisterEntityAccount';
import WizardFormSecondPage from './SettingPassword';
import WizardFormThirdPage from './VerifyBusinessEmail';

import { Stepper, Step, StepLabel, CircularProgress, Typography, useTheme, useMediaQuery } from '@mui/material';
import { styled } from '@mui/material/styles';
import StepConnector, { stepConnectorClasses } from '@mui/material/StepConnector';
import { StepIconProps } from '@mui/material/StepIcon';
import { useDispatch, useSelector } from 'react-redux';
import { RootState } from '../../store';
import { reduxForm, InjectedFormProps } from 'redux-form';
import { setwizardPageNumber, setWizardStepValidation } from 'MyComponents/EntityAccountReducer';
import Header from 'MyComponents/Includes/Header';
import Footer from 'MyComponents/Includes/Footer';

interface Props {}

const defaultSteps = ['Entity', 'Password', 'Verify', 'Welcome'];

const CustomConnector = styled(StepConnector)(() => ({
  [`&.${stepConnectorClasses.alternativeLabel}`]: {
    top: 12,
  },
  [`&.${stepConnectorClasses.active}`]: {
    [`& .${stepConnectorClasses.line}`]: {
      borderColor: '#e0e0e0',
    },
  },
  [`&.${stepConnectorClasses.completed}`]: {
    [`& .${stepConnectorClasses.line}`]: {
      borderColor: '#e0e0e0',
    },
  },
  [`& .${stepConnectorClasses.line}`]: {
    borderColor: '#e0e0e0',
    borderTopWidth: 2,
    borderRadius: 1,
  },
}));

const CustomStepIconRoot = styled('div')<{ ownerState: { completed?: boolean; active?: boolean; error?: boolean } }>(
  ({ theme, ownerState }) => ({
    backgroundColor: ownerState.active || ownerState.completed ? '#00D084' : '#A9B5C1',
    zIndex: 1,
    color: '#fff',
    width: 24,
    height: 24,
    display: 'flex',
    borderRadius: '50%',
    justifyContent: 'center',
    alignItems: 'center',
    fontSize: '0.75rem',
    fontWeight: 600,
    ...(ownerState.error && {
      backgroundColor: '#f44336',
    }),
    [theme.breakpoints.up('md')]: {
      ...( (ownerState.active || ownerState.completed) && {
        backgroundColor: 'rgba(0, 0, 0, 0.2)',
        color: '#fff',
      }),
      ...( ownerState.error && {
        backgroundColor: 'rgba(0, 0, 0, 0.2)',
        color: '#fff',
      }),
    },
  }),
);

function CustomStepIcon(props: StepIconProps) {
  const { active, completed, error, className, icon } = props;
  return (
    <CustomStepIconRoot ownerState={{ completed, active, error }} className={className}>
      {completed ? '✓' : String(icon)}
    </CustomStepIconRoot>
  );
}

const CustomStepperContainer = styled('div')(({ theme }) => ({
  width: '100%',
  maxWidth: 1000,
  margin: '0 auto',
  padding: '24px 20px 16px 20px',
  [theme.breakpoints.down('md')]: {
    position: 'sticky',
    top: 72,
    zIndex: 1010,
    backgroundColor: 'var(--bs-body-bg, #fff)',
    borderBottom: '1px solid rgba(0,0,0,0.05)',
    paddingTop: '20px',
    paddingBottom: '8px',
  }
}));

const WizardEntityAccount: React.FC<InjectedFormProps<FormData>> = () => {
  const dispatch = useDispatch();
  const page = useSelector((state: RootState) => state.entityAccount.wizardPageNumber);
  const themeObj = useTheme();
  const isMobile = useMediaQuery(themeObj.breakpoints.down('md'));

  const desktopSteps = ['Entity Information', 'Set Password', 'Verify Business Email', 'Welcome'];
  const steps = isMobile ? defaultSteps : desktopSteps;
  const stepValidation = useSelector((state: RootState) => state.entityAccount.wizardStepValidation);

  const isStepFailed = (step: number) => !stepValidation[step];

  const renderStepComponent = () => {
    switch (page) {
      case 1: return <WizardFormFirstPage />;
      case 2: return <WizardFormSecondPage />;
      case 3: return <WizardFormThirdPage />;
      default: return <div>Invalid Page</div>;
    }
  };

  return (
    <>
      <Header />
      <CustomStepperContainer>
        <Stepper activeStep={page - 1} alternativeLabel={isMobile} connector={<CustomConnector />}>
          {steps.map((label, index) => {
            const hasError = isStepFailed(index + 1);
            const isActiveOrCompleted = index <= page - 1;
            return (
            <Step key={label}>
              <StepLabel
                error={hasError}
                slots={{ stepIcon: CustomStepIcon }}
                sx={{
                  ...(isMobile ? {
                    '& .MuiStepLabel-label': {
                      fontSize: { xs: '0.65rem', sm: '0.75rem' },
                      color: '#A9B5C1',
                      marginTop: '4px',
                    },
                    '& .MuiStepLabel-label.Mui-active': {
                      color: '#00D084',
                      fontWeight: 600,
                    },
                    '& .MuiStepLabel-label.Mui-completed': {
                      color: '#707070',
                      fontWeight: 500,
                    },
                    '& .MuiStepLabel-label.Mui-error': {
                      color: '#f44336',
                    },
                  } : {
                    backgroundColor: hasError ? '#f44336' : (isActiveOrCompleted ? '#00D084' : '#f0f0f0'),
                    padding: '6px 20px 6px 6px',
                    borderRadius: '50px',
                    '& .MuiStepLabel-label': {
                      fontSize: '0.85rem',
                      color: isActiveOrCompleted ? '#fff !important' : '#707070',
                      fontWeight: 600,
                      marginLeft: '2px',
                    }
                  })
                }}
              >
                {label}
              </StepLabel>
            </Step>
            );
          })}
        </Stepper>
      </CustomStepperContainer>

      <div className="d-flex flex-column flex-grow-1 mx-auto w-100" style={{ maxWidth: '1920px' }}>
        {renderStepComponent()}
      </div>
      <Footer />
    </>
  );
};

const WrappedWizardEntityAccount = reduxForm<FormData, Props>({
  form: 'entityWizard',
})(WizardEntityAccount);

export default WrappedWizardEntityAccount;
